import { join } from 'path';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import log from 'loglevel';
import { CONFIG } from '../config';
import {
  getUserTokens, isTokenExpired,
} from '../utils';
import {  HTTP_STATUSES } from '../consts/apiCodes';
import { API_ROUTES } from '../consts';
import { refresh as refreshToken, logoutAfterError } from './refreshToken';

const TAG = '[AXIOS_INSTANCE]';

const axiosInstance = axios.create({
  baseURL: CONFIG.BASE_URL,
  timeout: CONFIG.HTTP_TIMEOUT_SECONDS * 1000,
});

// Interfaces
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  metadata: {
    startTime?: Date,
    endTime?: Date,
  }
}

// Helper Functions
const calculateResponseDuration = (requestConfig: CustomAxiosRequestConfig): number => {
  const endTime = requestConfig.metadata.endTime?.getTime();
  const startTime = requestConfig.metadata.startTime?.getTime();

  if (endTime && startTime) {
    return endTime - startTime;
  }

  return -1;
};

const logResponseTime = (response: AxiosResponse): void => {
  const requestConfig = response.config as CustomAxiosRequestConfig;
  if (requestConfig.metadata) {
    requestConfig.metadata.endTime = new Date();

    const responseDuration = calculateResponseDuration(requestConfig);

    log.debug(
      TAG,
      'The response time from',
      response.config.url,
      'is',
      (responseDuration > -1 ? `${responseDuration}ms` : 'unknown response time'),
    );
  }
};

axiosInstance.interceptors.request.use(
  async (request: AxiosRequestConfig) => {
    log.debug(TAG, 'new request:', request);

    // add timestamp to this request
    const requestConfig = request as CustomAxiosRequestConfig;
    requestConfig.metadata = { startTime: new Date() };
    requestConfig.url = join(CONFIG.PREFIX_URL, requestConfig.url || '');

    const tokens = getUserTokens();
    if (isTokenExpired() && tokens) {
      return refreshToken({
        requestConfig,
        refreshToken: tokens.refreshToken,
      });
    }

    if (tokens) {
      requestConfig.headers.Authorization = `${tokens.tokenType} ${tokens.accessToken}`;
    }

    return requestConfig;
  },
  (error) => {
    log.warn(TAG, 'Got a request error:', error);
  },
);

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    logResponseTime(response);
    log.debug(TAG, 'response:', response);

    return response;
  },
  (errorResponse) => {
    logResponseTime(errorResponse as AxiosResponse);
    log.warn(TAG, 'Got a response error:', errorResponse);

    // we want to redirect user to login page if respond UNAUTHORIZED(401) user
    // but need to exclude scenario for login error, no need to redirect from login page to login page
    if (
      errorResponse?.response?.status === HTTP_STATUSES.UNAUTHORIZED &&
      errorResponse?.response?.config?.url !== join(CONFIG.PREFIX_URL, API_ROUTES.LOGIN)
    ) {
      logoutAfterError();
    }

    return Promise.reject(errorResponse);
  },
);

export default axiosInstance;
